import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './BirdCalls.css';

// Import all bird audio files
import SkylarkAudio from '../assets/audio/Skylark.m4a';
import RedstartAudio from '../assets/audio/Redstart.m4a';
import SwallowAudio from '../assets/audio/Swallow.m4a';
import SongThrushAudio from '../assets/audio/Song thrush.m4a';
import RobinAudio from '../assets/audio/Robin.m4a';
import BlackbirdAudio from '../assets/audio/Blackbird .m4a';
import YellowhammerAudio from '../assets/audio/Yellowhammer.m4a';
import WrenAudio from '../assets/audio/Wren.m4a';
import ChiffchaffAudio from '../assets/audio/Chiffchaff.m4a';
import HouseSparrowAudio from '../assets/audio/House sparrow.m4a';
import CollaredDoveAudio from '../assets/audio/Collared dove.m4a';
import StarlingAudio from '../assets/audio/Starling.m4a';

interface BirdData {
  name: string;
  audio: string;
  notes: string;
  taxonomy: string;
  habitat: string;
  heard: string;
  seen: string;
  diet: string;
  adults: string;
  length: string;
  earlyBirdTime: string;
}

// Additional bird names for hard mode autocomplete
const additionalBirds = [
  'Blue Tit', 'Great Tit', 'Coal Tit', 'Goldfinch', 'Greenfinch', 'Chaffinch',
  'Bullfinch', 'Siskin', 'Linnet', 'Goldcrest', 'Firecrest', 'Nuthatch',
  'Treecreeper', 'Jay', 'Magpie', 'Crow', 'Raven', 'Jackdaw', 'Rook',
  'Woodpigeon', 'Stock Dove', 'Turtle Dove', 'Cuckoo', 'Swift', 'Kingfisher',
  'Green Woodpecker', 'Great Spotted Woodpecker', 'Lesser Spotted Woodpecker',
  'Barn Owl', 'Tawny Owl', 'Little Owl', 'Kestrel', 'Sparrowhawk', 'Buzzard',
  'Red Kite', 'Peregrine Falcon', 'Hobby', 'Merlin'
];

type DifficultyLevel = 'hatchling' | 'fledgling' | 'mature';
type GameState = 'menu' | 'playing' | 'finished' | 'birdInfo';

const BirdCalls: React.FC = () => {
  const { t } = useTranslation();
  const [gameState, setGameState] = useState<GameState>('menu');
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('hatchling');
  const [currentBirdIndex, setCurrentBirdIndex] = useState(0);
  const [shuffledBirds, setShuffledBirds] = useState<BirdData[]>([]);
  const [score, setScore] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedBird, setSelectedBird] = useState<BirdData | null>(null);
  const [options, setOptions] = useState<BirdData[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<string[]>([]);
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);

  // Generate birds array from translations
  const birds: BirdData[] = [
    {
      name: t('birdCalls.birds.skylark.name'),
      audio: SkylarkAudio,
      notes: t('birdCalls.birds.skylark.notes'),
      taxonomy: t('birdCalls.birds.skylark.taxonomy'),
      habitat: t('birdCalls.birds.skylark.habitat'),
      heard: t('birdCalls.birds.skylark.heard'),
      seen: t('birdCalls.birds.skylark.seen'),
      diet: t('birdCalls.birds.skylark.diet'),
      adults: t('birdCalls.birds.skylark.adults'),
      length: t('birdCalls.birds.skylark.length'),
      earlyBirdTime: t('birdCalls.birds.skylark.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.redstart.name'),
      audio: RedstartAudio,
      notes: t('birdCalls.birds.redstart.notes'),
      taxonomy: 'Phoenicurus phoenicurus',
      habitat: t('birdCalls.birds.redstart.habitat'),
      heard: t('birdCalls.birds.redstart.heard'),
      seen: t('birdCalls.birds.redstart.seen'),
      diet: t('birdCalls.birds.redstart.diet'),
      adults: t('birdCalls.birds.redstart.adults'),
      length: t('birdCalls.birds.redstart.length'),
      earlyBirdTime: t('birdCalls.birds.redstart.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.swallow.name'),
      audio: SwallowAudio,
      notes: t('birdCalls.birds.swallow.notes'),
      taxonomy: 'Hirundo rustica',
      habitat: t('birdCalls.birds.swallow.habitat'),
      heard: t('birdCalls.birds.swallow.heard'),
      seen: t('birdCalls.birds.swallow.seen'),
      diet: t('birdCalls.birds.swallow.diet'),
      adults: t('birdCalls.birds.swallow.adults'),
      length: t('birdCalls.birds.swallow.length'),
      earlyBirdTime: t('birdCalls.birds.swallow.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.songThrush.name'),
      audio: SongThrushAudio,
      notes: t('birdCalls.birds.songThrush.notes'),
      taxonomy: 'Turdus philomelos',
      habitat: t('birdCalls.birds.songThrush.habitat'),
      heard: t('birdCalls.birds.songThrush.heard'),
      seen: t('birdCalls.birds.songThrush.seen'),
      diet: t('birdCalls.birds.songThrush.diet'),
      adults: t('birdCalls.birds.songThrush.adults'),
      length: t('birdCalls.birds.songThrush.length'),
      earlyBirdTime: t('birdCalls.birds.songThrush.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.robin.name'),
      audio: RobinAudio,
      notes: t('birdCalls.birds.robin.notes'),
      taxonomy: 'Erithacus rubecula',
      habitat: t('birdCalls.birds.robin.habitat'),
      heard: t('birdCalls.birds.robin.heard'),
      seen: t('birdCalls.birds.robin.seen'),
      diet: t('birdCalls.birds.robin.diet'),
      adults: t('birdCalls.birds.robin.adults'),
      length: t('birdCalls.birds.robin.length'),
      earlyBirdTime: t('birdCalls.birds.robin.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.blackbird.name'),
      audio: BlackbirdAudio,
      notes: t('birdCalls.birds.blackbird.notes'),
      taxonomy: 'Turdus merula',
      habitat: t('birdCalls.birds.blackbird.habitat'),
      heard: t('birdCalls.birds.blackbird.heard'),
      seen: t('birdCalls.birds.blackbird.seen'),
      diet: t('birdCalls.birds.blackbird.diet'),
      adults: t('birdCalls.birds.blackbird.adults'),
      length: t('birdCalls.birds.blackbird.length'),
      earlyBirdTime: t('birdCalls.birds.blackbird.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.yellowhammer.name'),
      audio: YellowhammerAudio,
      notes: t('birdCalls.birds.yellowhammer.notes'),
      taxonomy: 'Emberiza citrinella',
      habitat: t('birdCalls.birds.yellowhammer.habitat'),
      heard: t('birdCalls.birds.yellowhammer.heard'),
      seen: t('birdCalls.birds.yellowhammer.seen'),
      diet: t('birdCalls.birds.yellowhammer.diet'),
      adults: t('birdCalls.birds.yellowhammer.adults'),
      length: t('birdCalls.birds.yellowhammer.length'),
      earlyBirdTime: t('birdCalls.birds.yellowhammer.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.wren.name'),
      audio: WrenAudio,
      notes: t('birdCalls.birds.wren.notes'),
      taxonomy: 'Troglodytes troglodytes',
      habitat: t('birdCalls.birds.wren.habitat'),
      heard: t('birdCalls.birds.wren.heard'),
      seen: t('birdCalls.birds.wren.seen'),
      diet: t('birdCalls.birds.wren.diet'),
      adults: t('birdCalls.birds.wren.adults'),
      length: t('birdCalls.birds.wren.length'),
      earlyBirdTime: t('birdCalls.birds.wren.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.chiffchaff.name'),
      audio: ChiffchaffAudio,
      notes: t('birdCalls.birds.chiffchaff.notes'),
      taxonomy: 'Phylloscopus collybita',
      habitat: t('birdCalls.birds.chiffchaff.habitat'),
      heard: t('birdCalls.birds.chiffchaff.heard'),
      seen: t('birdCalls.birds.chiffchaff.seen'),
      diet: t('birdCalls.birds.chiffchaff.diet'),
      adults: t('birdCalls.birds.chiffchaff.adults'),
      length: t('birdCalls.birds.chiffchaff.length'),
      earlyBirdTime: t('birdCalls.birds.chiffchaff.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.houseSparrow.name'),
      audio: HouseSparrowAudio,
      notes: t('birdCalls.birds.houseSparrow.notes'),
      taxonomy: 'Passer domesticus',
      habitat: t('birdCalls.birds.houseSparrow.habitat'),
      heard: t('birdCalls.birds.houseSparrow.heard'),
      seen: t('birdCalls.birds.houseSparrow.seen'),
      diet: t('birdCalls.birds.houseSparrow.diet'),
      adults: t('birdCalls.birds.houseSparrow.adults'),
      length: t('birdCalls.birds.houseSparrow.length'),
      earlyBirdTime: t('birdCalls.birds.houseSparrow.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.collaredDove.name'),
      audio: CollaredDoveAudio,
      notes: t('birdCalls.birds.collaredDove.notes'),
      taxonomy: 'Streptopelia decaocto',
      habitat: t('birdCalls.birds.collaredDove.habitat'),
      heard: t('birdCalls.birds.collaredDove.heard'),
      seen: t('birdCalls.birds.collaredDove.seen'),
      diet: t('birdCalls.birds.collaredDove.diet'),
      adults: t('birdCalls.birds.collaredDove.adults'),
      length: t('birdCalls.birds.collaredDove.length'),
      earlyBirdTime: t('birdCalls.birds.collaredDove.earlyBirdTime')
    },
    {
      name: t('birdCalls.birds.starling.name'),
      audio: StarlingAudio,
      notes: t('birdCalls.birds.starling.notes'),
      taxonomy: 'Sturnus vulgaris',
      habitat: t('birdCalls.birds.starling.habitat'),
      heard: t('birdCalls.birds.starling.heard'),
      seen: t('birdCalls.birds.starling.seen'),
      diet: t('birdCalls.birds.starling.diet'),
      adults: t('birdCalls.birds.starling.adults'),
      length: t('birdCalls.birds.starling.length'),
      earlyBirdTime: t('birdCalls.birds.starling.earlyBirdTime')
    }
  ];

  // Shuffle array utility
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Start game
  const startGame = (selectedDifficulty: DifficultyLevel) => {
    setDifficulty(selectedDifficulty);
    setGameState('playing');
    setCurrentBirdIndex(0);
    setScore(0);
    setUserAnswer('');
    setShowAnswer(false);
    setSelectedBird(null);
    
    const shuffled = shuffleArray(birds);
    setShuffledBirds(shuffled);
    
    // Setup options for current bird
    setupOptions(shuffled[0], selectedDifficulty);
  };

  // Setup options based on difficulty
  const setupOptions = (currentBird: BirdData, difficultyLevel: DifficultyLevel) => {
    if (difficultyLevel === 'hatchling') {
      // Easy: 3 options
      const otherBirds = birds.filter(bird => bird.name !== currentBird.name);
      const randomBirds = shuffleArray(otherBirds).slice(0, 2);
      const optionSet = shuffleArray([currentBird, ...randomBirds]);
      setOptions(optionSet);
    } else if (difficultyLevel === 'fledgling') {
      // Medium: all 12 birds
      setOptions(shuffleArray(birds));
    }
    // Hard mode doesn't use options
  };

  // Play audio
  const playAudio = () => {
    if (audioRef.current) {
      setIsPlaying(true);
      audioRef.current.currentTime = 0; // Reset to beginning
      audioRef.current.play()
        .then(() => {
          audioRef.current!.onended = () => setIsPlaying(false);
        })
        .catch(err => {
          console.error('Audio play error:', err);
          setIsPlaying(false);
        });
    }
  };

  // Reset audio player
  const resetAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  // Handle answer submission
  const submitAnswer = (answer: string) => {
    const currentBird = shuffledBirds[currentBirdIndex];
    const isCorrect = answer.toLowerCase() === currentBird.name.toLowerCase();
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    setUserAnswer(answer); // Set the user's answer for display
    setShowAnswer(true);
    
    // For easy mode, highlight correct answer if wrong
    if (difficulty === 'hatchling' && !isCorrect) {
      // Answer display is handled in the render
    }
    
    // Don't auto-advance - wait for user to click continue
  };

  // Move to next bird
  const nextBird = () => {
    if (currentBirdIndex < shuffledBirds.length - 1) {
      const nextIndex = currentBirdIndex + 1;
      setCurrentBirdIndex(nextIndex);
      setUserAnswer('');
      setShowAnswer(false);
      setAutoCompleteOptions([]);
      setShowAutoComplete(false);
      setupOptions(shuffledBirds[nextIndex], difficulty);
    } else {
      setGameState('finished');
    }
  };

  // Handle autocomplete for hard mode
  const handleInputChange = (value: string) => {
    setUserAnswer(value);
    
    if (value.length > 1) {
      const allBirdNames = [...birds.map(b => b.name), ...additionalBirds];
      const matches = allBirdNames.filter(name => 
        name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      
      setAutoCompleteOptions(matches);
      setShowAutoComplete(matches.length > 0);
    } else {
      setShowAutoComplete(false);
    }
  };

  // Select autocomplete option
  const selectAutoComplete = (option: string) => {
    setUserAnswer(option);
    setShowAutoComplete(false);
  };

  // Show bird information
  const showBirdInfo = (bird: BirdData) => {
    setSelectedBird(bird);
    setGameState('birdInfo');
  };

  // Reset game
  const resetGame = () => {
    setGameState('menu');
    setCurrentBirdIndex(0);
    setScore(0);
    setUserAnswer('');
    setShowAnswer(false);
    setSelectedBird(null);
    setIsPlaying(false);
  };

  const currentBird = shuffledBirds[currentBirdIndex];
  const progress = shuffledBirds.length > 0 ? ((currentBirdIndex + 1) / shuffledBirds.length) * 100 : 0;

  return (
    <div className="bird-calls-game">
      <div className="game-header">
        <h2>🎵 {t('birdCalls.title')}</h2>
        <p>{t('birdCalls.subtitle')}</p>
      </div>

      {gameState === 'menu' && (
        <div className="game-menu">
          <h3>{t('birdCalls.chooseLevel')}</h3>
          <div className="difficulty-buttons">
            <button 
              className="difficulty-btn hatchling"
              onClick={() => startGame('hatchling')}
            >
              🥚 {t('birdCalls.difficulties.hatchling.title')}
              <span className="difficulty-desc">{t('birdCalls.difficulties.hatchling.description')}</span>
            </button>
            <button 
              className="difficulty-btn fledgling"
              onClick={() => startGame('fledgling')}
            >
              🐣 {t('birdCalls.difficulties.fledgling.title')}
              <span className="difficulty-desc">{t('birdCalls.difficulties.fledgling.description')}</span>
            </button>
            <button 
              className="difficulty-btn mature"
              onClick={() => startGame('mature')}
            >
              🦅 {t('birdCalls.difficulties.mature.title')}
              <span className="difficulty-desc">{t('birdCalls.difficulties.mature.description')}</span>
            </button>
          </div>
        </div>
      )}

      {gameState === 'playing' && currentBird && (
        <div className="game-playing">
          <div className="game-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              {t('birdCalls.game.bird')} {currentBirdIndex + 1} {t('birdCalls.game.of')} {shuffledBirds.length} | {t('birdCalls.game.score')}: {score}
            </span>
          </div>

          <div className="audio-section">
            <audio ref={audioRef} src={currentBird.audio} />
            <div className="audio-controls">
              <button 
                className={`play-button ${isPlaying ? 'playing' : ''}`}
                onClick={playAudio}
                disabled={isPlaying}
              >
                {isPlaying ? '🔊 ' + t('birdCalls.game.playingButton') : '🎵 ' + t('birdCalls.game.playButton')}
              </button>
              {isPlaying && (
                <button 
                  className="reset-audio-btn"
                  onClick={resetAudio}
                  title="Stop audio"
                >
                  ⏹️
                </button>
              )}
            </div>
          </div>

          {!showAnswer && (
            <div className="answer-section">
              {difficulty === 'mature' ? (
                // Hard mode: text input with autocomplete
                <div className="text-input-section">
                  <h3>{t('birdCalls.game.typeQuestion')}</h3>
                  <div className="autocomplete-container">
                    <input
                      type="text"
                      value={userAnswer}
                      onChange={(e) => handleInputChange(e.target.value)}
                      placeholder={t('birdCalls.game.placeholder')}
                      className="bird-input"
                    />
                    {showAutoComplete && (
                      <div className="autocomplete-dropdown">
                        {autoCompleteOptions.map((option, index) => (
                          <div
                            key={index}
                            className="autocomplete-option"
                            onClick={() => selectAutoComplete(option)}
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    className="submit-btn"
                    onClick={() => submitAnswer(userAnswer)}
                    disabled={!userAnswer.trim()}
                  >
                    {t('birdCalls.game.submitAnswer')}
                  </button>
                </div>
              ) : (
                // Easy and Medium modes: multiple choice
                <div className="multiple-choice">
                  <h3>{t('birdCalls.game.question')}</h3>
                  <div className="options-grid">
                    {options.map((option, index) => (
                      <button
                        key={index}
                        className="option-btn"
                        onClick={() => submitAnswer(option.name)}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {showAnswer && (
            <div className="answer-result">
              <div className={`result-message ${
                userAnswer.toLowerCase() === currentBird.name.toLowerCase() ? 'correct' : 'incorrect'
              }`}>
                {userAnswer.toLowerCase() === currentBird.name.toLowerCase() ? (
                  <span>✅ {t('birdCalls.game.correct')} {currentBird.name}</span>
                ) : (
                  <span>❌ {t('birdCalls.game.incorrect')} {currentBird.name}</span>
                )}
              </div>
              
              {difficulty === 'hatchling' && userAnswer.toLowerCase() !== currentBird.name.toLowerCase() && (
                <div className="correct-highlight">
                  <div className="options-grid">
                    {options.map((option, index) => (
                      <button
                        key={index}
                        className={`option-btn ${option.name === currentBird.name ? 'correct-answer' : 'incorrect-option'}`}
                        disabled
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="answer-buttons">
                <button 
                  className="bird-info-btn"
                  onClick={() => showBirdInfo(currentBird)}
                >
                  {t('birdCalls.game.learnMore')} {currentBird.name}
                </button>
                <button 
                  className="continue-btn"
                  onClick={nextBird}
                >
                  {currentBirdIndex < shuffledBirds.length - 1 ? t('birdCalls.game.nextBird') : t('birdCalls.game.finishGame')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {gameState === 'finished' && (
        <div className="game-finished">
          <h3>🎉 {t('birdCalls.gameComplete.title')}</h3>
          <div className="final-score">
            <span className="score-text">{t('birdCalls.gameComplete.yourScore')}: {score} {t('birdCalls.gameComplete.outOf')} {shuffledBirds.length}</span>
            <div className="score-percentage">
              {Math.round((score / shuffledBirds.length) * 100)}%
            </div>
          </div>
          
          <div className="score-message">
            {score === shuffledBirds.length && "🏆 " + t('birdCalls.gameComplete.messages.perfect')}
            {score >= shuffledBirds.length * 0.8 && score < shuffledBirds.length && "🎵 " + t('birdCalls.gameComplete.messages.excellent')}
            {score >= shuffledBirds.length * 0.6 && score < shuffledBirds.length * 0.8 && "👍 " + t('birdCalls.gameComplete.messages.good')}
            {score < shuffledBirds.length * 0.6 && "🌱 " + t('birdCalls.gameComplete.messages.start')}
          </div>

          <div className="final-buttons">
            <button className="play-again-btn" onClick={resetGame}>
              {t('birdCalls.gameComplete.playAgain')}
            </button>
            <button className="review-birds-btn" onClick={() => setGameState('menu')}>
              {t('birdCalls.gameComplete.reviewBirds')}
            </button>
          </div>
        </div>
      )}

      {gameState === 'birdInfo' && selectedBird && (
        <div className="bird-info">
          <div className="bird-info-header">
            <h3>🐦 {selectedBird.name}</h3>
            <button className="close-btn" onClick={() => setGameState('playing')}>
              ✕
            </button>
          </div>
          
          <div className="bird-details">
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.notes')}:</strong> {selectedBird.notes}
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.scientificName')}:</strong> <em>{selectedBird.taxonomy}</em>
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.habitat')}:</strong> {selectedBird.habitat}
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.whenHeard')}:</strong> {selectedBird.heard}
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.whereSeen')}:</strong> {selectedBird.seen}
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.diet')}:</strong> {selectedBird.diet}
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.appearance')}:</strong> {selectedBird.adults}
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.length')}:</strong> {selectedBird.length}
            </div>
            <div className="bird-detail">
              <strong>{t('birdCalls.birdInfo.dawnChorus')}:</strong> {selectedBird.earlyBirdTime}
            </div>
          </div>

          <div className="bird-audio-replay">
            <audio ref={audioRef} src={selectedBird.audio} />
            <div className="audio-controls">
              <button 
                className={`play-button ${isPlaying ? 'playing' : ''}`}
                onClick={playAudio}
                disabled={isPlaying}
              >
                {isPlaying ? '🔊 ' + t('birdCalls.game.playingButton') : '🎵 ' + t('birdCalls.game.playButton')}
              </button>
              {isPlaying && (
                <button 
                  className="reset-audio-btn"
                  onClick={resetAudio}
                  title={t('birdCalls.game.stopButton')}
                >
                  ⏹️
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BirdCalls; 